import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * VideoAvatar — modo "doblaje" sin lipsync real.
 *
 * El video se reproduce en mute mientras `isSpeaking=true` (controlado por
 * App.jsx vía isTtsPlaying) y se pausa volviendo al primer frame cuando el
 * audio del TTS termina. La ilusión es buena porque el audio del simulador
 * suena en paralelo y la boca del personaje en el video se mueve.
 *
 * Drop-in con la misma firma de props que LemonSliceAvatar (isSpeaking,
 * isListening, isThinking, size). Expone `pushAudio` / `attachAudioElement`
 * / `clearBuffer` como no-ops para no romper los call-sites de App.jsx
 * que usan el avatarRef compartido, pero internamente NO intercepta el
 * audio — App.jsx ya lo reproduce localmente.
 */

const ringColorFor = (isSpeaking, isListening) => {
    if (isSpeaking) return '#22d3ee';
    if (isListening) return '#a3e635';
    return '#e879f9';
};

const VideoAvatar = forwardRef(function VideoAvatar(
    { isSpeaking = false, isListening = false, isThinking = false, size = 360, videoUrl = '', onReady = null },
    ref,
) {
    const videoRef = useRef(null);
    const ringColor = ringColorFor(isSpeaking, isListening);
    // Loader interno hasta que el <video> tenga datos suficientes para
    // empezar a reproducir (canplay/loadeddata). Si no hay videoUrl, no
    // hay nada que cargar — pasamos a "listo" inmediatamente.
    const [isLoading, setIsLoading] = useState(!!videoUrl);

    useEffect(() => {
        if (!videoUrl) {
            setIsLoading(false);
            onReady?.();
            return;
        }
        setIsLoading(true);
    }, [videoUrl, onReady]);

    // Sincroniza play/pause del video con el estado de habla del avatar.
    // Cuando termina el TTS volvemos al primer frame para dar feeling de
    // "personaje en silencio listo para volver a hablar".
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !videoUrl) return;
        if (isSpeaking) {
            try { v.play().catch(() => {}); } catch (_) {}
        } else {
            try {
                v.pause();
                v.currentTime = 0;
            } catch (_) {}
        }
    }, [isSpeaking, videoUrl]);

    const handleVideoReady = () => {
        if (!isLoading) return;
        setIsLoading(false);
        onReady?.();
    };

    // No-ops: VideoAvatar no intercepta audio. El TTS lo toca App.jsx
    // localmente (mismo path que el AnimatedAvatar fallback). Solo
    // devolvemos la firma esperada para no romper los call-sites de
    // avatarRef.current.pushAudio() / attachAudioElement().
    useImperativeHandle(ref, () => ({
        pushAudio: async () => 0,
        attachAudioElement: async () => {},
        clearBuffer: () => {},
    }), []);

    return (
        <div
            className="relative flex items-center justify-center select-none"
            style={{ width: size, height: size }}
            data-tour="avatar"
        >
            {(isSpeaking || isListening) && (
                <>
                    <div className="absolute rounded-full animate-ping" style={{ inset: -8, background: `${ringColor}1A`, animationDuration: '1.8s' }} />
                    <div className="absolute rounded-full animate-ping" style={{ inset: -18, background: `${ringColor}0D`, animationDuration: '2.4s', animationDelay: '0.5s' }} />
                </>
            )}

            <div
                className="absolute inset-0 rounded-full overflow-hidden"
                style={{
                    background: 'linear-gradient(150deg, #181830 0%, #0f0f1f 100%)',
                    border: `1.5px solid ${ringColor}80`,
                    boxShadow: `inset 0 0 24px rgba(34, 211, 238, 0.08), 0 0 24px ${ringColor}33`,
                }}
            >
                {videoUrl ? (
                    <>
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            muted
                            playsInline
                            loop
                            preload="auto"
                            onCanPlay={handleVideoReady}
                            onLoadedData={handleVideoReady}
                            onError={handleVideoReady}
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                                filter: 'contrast(1.05) saturate(1.1)',
                                // El recorte de object-cover queda anclado arriba
                                // del frame del video: si la altura del círculo
                                // no coincide con la del video, se corta por
                                // abajo (no por la cabeza del personaje).
                                objectPosition: 'center top',
                            }}
                        />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: `radial-gradient(ellipse 60% 40% at 50% 100%, ${ringColor}22 0%, transparent 60%)`,
                            }}
                        />
                        {isLoading && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-bg-900/60 backdrop-blur-sm">
                                <Loader2 className="w-7 h-7 text-neon-cyan animate-spin mb-2" />
                                <span className="text-[9px] font-mono tracking-widest uppercase text-neon-cyan/80">
                                    cargando…
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div
                            className="w-16 h-16 rounded-full mb-3"
                            style={{
                                background: `radial-gradient(circle at 30% 30%, ${ringColor}aa, ${ringColor}22)`,
                                boxShadow: `0 0 24px ${ringColor}55, inset 0 0 12px ${ringColor}33`,
                                animation: 'glow-breathe 3s ease-in-out infinite',
                            }}
                        />
                        <span className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: ringColor, opacity: 0.7 }}>
                            no video
                        </span>
                    </div>
                )}
            </div>

            {/* Status dot inferior derecha */}
            <div
                className="absolute rounded-full"
                style={{
                    width: 14,
                    height: 14,
                    right: '8%',
                    bottom: '8%',
                    background: isThinking ? '#F59E0B' : isSpeaking ? '#22d3ee' : isListening ? '#a3e635' : '#10B981',
                    boxShadow: isThinking
                        ? '0 0 12px rgba(245, 158, 11, 0.7)'
                        : `0 0 12px ${ringColor}aa`,
                    transition: 'all 0.3s ease',
                }}
            />
        </div>
    );
});

export default VideoAvatar;
